import { selectedTrainSchema } from "~/features/train-info/schemas";
import { StationsSchema } from "../stations/schemas";

import * as localforage from "localforage";
import type {
  ZodEffects,
  ZodNullable,
  ZodSchema,
  ZodType,
  ZodTypeDef,
} from "zod";
import { z } from "zod";

type ZodLocalStorageType<T> = ZodNullable<ZodType<T, ZodTypeDef, unknown>>;

type Schema<T> = ZodEffects<ZodLocalStorageType<T>> | ZodLocalStorageType<T>;

export const SHARED_LOCAL_STORAGE_UPATED_EVENT =
  "SHARED_LOCAL_STORAGE_UPATED_EVENT";

function createSharedLocalStorageBroadcastChannel() {
  const channel = new BroadcastChannel("shared-local-storage");

  return {
    postMessage: (key: string) => channel.postMessage(key),
    subscribe: (callback: (event: MessageEvent<string>) => void) => {
      channel.addEventListener("message", callback);
      return () => channel.removeEventListener("message", callback);
    },
  };
}

export const SHARED_LOCAL_STORAGE_BROADCAST_CHANNEL =
  createSharedLocalStorageBroadcastChannel();

class SharedLocalStorageItem<T> {
  private readonly schema: Schema<T>;
  readonly key: string;

  constructor(key: string, schema: Schema<T>) {
    this.key = key;
    this.schema = schema;
  }

  /**
   * Notifies all contexts (tabs, service workers) about storage changes.
   *
   * Uses two notification mechanisms:
   * 1. BroadcastChannel - for cross-context communication (tabs, service workers)
   * 2. StorageEvent - for in-window listeners (only in browser context)
   *
   * This ensures that all parts of the application stay synchronized when
   * storage values change, regardless of where the change originated.
   */
  private notifyListeners() {
    SHARED_LOCAL_STORAGE_BROADCAST_CHANNEL.postMessage(this.key);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new StorageEvent(SHARED_LOCAL_STORAGE_UPATED_EVENT, {
          key: this.key,
        }),
      );
    }
  }

  async get(): Promise<T | null> {
    const value = await localforage.getItem(this.key);
    if (value) {
      const res = this.schema.safeParse(value);
      if (res.success) {
        return res.data;
      }
      await this.remove();
    }
    return null;
  }

  async set(value: T): Promise<void> {
    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === "string") {
      await localforage.setItem(this.key, value);
    } else {
      await localforage.setItem(this.key, JSON.stringify(value));
    }
    this.notifyListeners();
  }

  async remove(): Promise<void> {
    await localforage.removeItem(this.key);
    this.notifyListeners();
  }
}

function jsonSchema<T>(schema: ZodSchema<T>) {
  return z.preprocess((it) => {
    if (it && typeof it === "string") {
      return JSON.parse(it);
    }
    return null;
  }, schema.nullable());
}

export const sharedLocalStorage = {
  androidAppId: new SharedLocalStorageItem(
    "androidAppId",
    z.string().nullable(),
  ),
  employeeId: new SharedLocalStorageItem("employeeId", z.string().nullable()),
  networkStatus: new SharedLocalStorageItem(
    "networkStatus",
    jsonSchema(
      z.object({
        value: z.enum(["online", "offline"]),
        since: z.date(),
      }),
    ),
  ),
  selectedTrain: new SharedLocalStorageItem(
    "selectedTrain",
    jsonSchema(selectedTrainSchema),
  ),
  stationSearchHistory: new SharedLocalStorageItem(
    "station-search-history",
    jsonSchema(StationsSchema),
  ),
  rollingStockSearchHistory: new SharedLocalStorageItem(
    "rolling-stock-search-history",
    jsonSchema(
      z.array(
        z.object({
          setId: z.string(),
          vehicles: z.array(z.string()),
        }),
      ),
    ),
  ),
  dayPlansOverrideDate: new SharedLocalStorageItem(
    "dayPlansOverrideDate",
    jsonSchema(z.coerce.date()),
  ),
  dayPlanModal: new SharedLocalStorageItem(
    "dayPlanModal",
    jsonSchema(
      z.object({
        date: z.coerce.date(),
        updatedAt: z.coerce.date(),
      }),
    ),
  ),
  deviceToken: new SharedLocalStorageItem("deviceToken", z.string().nullable()),
  devtoolsButtonPosition: new SharedLocalStorageItem(
    "devtools-button-position",
    jsonSchema(
      z.object({
        edge: z.enum(["right", "left"]),
        offset: z.number(),
      }),
    ),
  ),
  timeSpentOfflineThisSession: new SharedLocalStorageItem(
    "timeSpentOfflineThisSession",
    jsonSchema(
      z.object({
        totalTimeSpentOffline: z.number(),
        timeSpentOfflineSplits: z.array(z.number()),
        rumSessionId: z.string().nullable().optional(),
        offlineSince: z.date().nullable().optional(),
        sessionDuration: z.number().optional(),
        sessionStart: z.coerce.date().optional(),
      }),
    ),
  ),
  shouldOverrideFidoHref: new SharedLocalStorageItem(
    "shouldOverrideFidoHref",
    jsonSchema(z.boolean().nullable()),
  ),
} as const;

export type { SharedLocalStorageItem };
